import { useState } from 'react';
import type { Gym } from '@jungles/shared';
import { useApi, useDebounced } from '../lib/hooks';
import { haptic } from '../lib/telegram';
import { Sheet } from './Sheet';
import { Loader } from './Chrome';
import { SearchIcon } from './icons';
import { GymLogo } from './GymLogo';

/** Сервер отдаёт текущий каталог; сохранённые legacy ID доступны отдельно. */

/** Город и адрес в одну строку: без адреса 184 «Lady Stretch» неразличимы. */
export function gymSubtitle(gym: Gym): string {
  const parts = [gym.city, gym.address].filter(Boolean);
  if (gym.membersCount) parts.push(`${gym.membersCount} в зачёте зала`);
  return parts.join(' · ') || 'Адрес не указан';
}

export function GymPicker({
  currentId,
  title = 'Выбрать зал',
  onPick,
  onClose,
}: {
  currentId: string | null;
  title?: string;
  onPick: (gym: Gym | null) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query);
  const needle = debounced.trim();

  const { data, loading, error } = useApi<Gym[]>(
    `/api/gyms?q=${encodeURIComponent(needle)}&limit=${needle ? 40 : 20}`,
  );
  // Уже выбранный зал может не попасть в выдачу — показываем его отдельной строкой.
  const { data: current } = useApi<Gym>(currentId ? `/api/gyms/${currentId}` : null);

  const gyms = (data ?? []).filter((gym) => gym.id !== currentId);

  const pick = (gym: Gym | null) => {
    haptic();
    onPick(gym);
  };

  const row = (gym: Gym, selected: boolean) => (
    <button
      key={gym.id}
      className={selected ? 'wr gym-catalog-row sel' : 'wr gym-catalog-row'}
      type="button"
      onClick={() => pick(gym)}
    >
      <GymLogo name={gym.name} logoUrl={gym.logoUrl} />
      <span className="wn">
        <b>{gym.name}</b>
        <small>{gymSubtitle(gym)}{gym.catalogStatus === 'legacy' ? ' · Сохранённый зал из прежнего каталога' : ''}</small>
      </span>
      <span className="chev">{selected ? '✓' : '+'}</span>
    </button>
  );

  return (
    <Sheet title={title} onClose={onClose} full>
      <label className="srch" style={{ marginTop: 10 }}>
        <SearchIcon />
        <span className="vh">Поиск зала</span>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Название, сеть или город"
          autoComplete="off"
        />
        {query ? (
          <button className="clr" type="button" onClick={() => setQuery('')} aria-label="Очистить">
            ×
          </button>
        ) : null}
      </label>

      <div style={{ flex: 1, overflowY: 'auto', marginTop: 12 }}>
        <div className="wl">
          <button
            className={currentId ? 'wr' : 'wr sel'}
            type="button"
            onClick={() => pick(null)}
          >
            <span className="gmn" aria-hidden="true" style={{ background: 'var(--surface)' }}>
              🏠
            </span>
            <span className="wn">
              <b>Без зала</b>
              <small>Дома или на улице</small>
            </span>
            <span className="chev">{currentId ? '' : '✓'}</span>
          </button>
          {current ? row(current, true) : null}
        </div>

        {loading ? <Loader /> : null}
        {error ? <p className="empty">{error}</p> : null}

        {gyms.length > 0 ? (
          <>
            <div className="sec">{needle ? 'Найдено' : 'Залы из каталога'}</div>
            <div className="wl">{gyms.map((gym) => row(gym, false))}</div>
          </>
        ) : null}

        {!loading && !error && gyms.length === 0 && needle ? (
          <p className="empty">
            Ничего не нашли. Попробуйте сеть или город — например, «ворлд класс» или «зенит
            екатеринбург»
          </p>
        ) : null}

        {!needle ? (
          <p className="nt" style={{ margin: '14px 2px 0' }}>
            В каталоге 2 861 зал — начните вводить название, сеть или город.
            Филиалы можно различить по адресу.
          </p>
        ) : null}

        <p className="nt" style={{ margin: '18px 2px 0' }}>
          Новый каталог собран из публичных карточек организаций. Права на данные и логотипы
          не подтверждены; логотип не означает партнёрство. Сохранённые залы прежнего
          каталога могут использовать данные © участников OpenStreetMap, лицензия ODbL.
        </p>
      </div>
    </Sheet>
  );
}
